import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { DialogLabels, MAX_ROWS_COUNT_ERROR, MAX_ROWS_COUNT_WARNING } from '../models/dx-tree-list.model';

@Injectable({
  providedIn: 'root',
})
export class OrdersConfirmDialogService {
  private readonly confirmService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);

  private readonly tableLabels: DialogLabels = {
    itemLabel: 'shared.folders',
    selectAllErrorTitle: 'shared.tableDataSourceMessges.selectAllErrorTitle',
    selectAllErrorSubtitle: 'shared.tableDataSourceMessges.selectAllErrorSubtitle',
    selectAllValidateErrorBtn: 'shared.tableDataSourceMessges.selectAllValidateErrorBtn',
    selectAllCancelWarningBtn: 'shared.tableDataSourceMessges.selectAllCancelWarningBtn',
    selectAllValidateWarningBtn: 'shared.tableDataSourceMessges.selectAllValidateWarningBtn',
    selectAllWarningSubtitle: 'shared.tableDataSourceMessges.selectAllWarningSubtitle',
  };

  showWarningDialog(
    totalCount: number,
    label: string,
    onValidate: () => void,
    onCancel: () => void,
  ): void {
    this.confirmService.confirm({
      validate: onValidate,
      cancel: onCancel,
      cancelBtnLabel: this.translateService.instant(this.tableLabels.selectAllCancelWarningBtn),
      validateBtnLabel: this.translateService.instant(this.tableLabels.selectAllValidateWarningBtn),
      iconName: 'type=danger',
      title: this.translateService.instant(this.tableLabels.selectAllErrorTitle, {
        totalCount,
        itemLabel: label,
      }),
      subtitle: this.translateService.instant(this.tableLabels.selectAllWarningSubtitle),
    });
  }

  // Avertissement NON bloquant « formule à 0,00 € » (abonnement) avant une annulation / un remboursement
  // PARTIEL (GEN-6319, cf. ticket « ANNULATION / REMBOURSEMENT PARTIEL »). Sur un abonnement, les séances
  // valent 0 € (prix porté par le package) → agir sur une partie ne change pas le montant de la formule ;
  // on informe l'utilisateur qui peut poursuivre ou renoncer. Le verbe du texte et le libellé du bouton de
  // validation s'adaptent à l'action ; bouton secondaire = « Annuler ». Affiché depuis le volet ET la liste.
  showZeroAmountFormulaWarning(
    action: 'cancel' | 'refund',
    onValidate: () => void,
    onCancel: () => void,
  ): void {
    const isRefund = action === 'refund';
    // Texte aligné mot pour mot sur le ticket (validation QA) : phrase de contexte + réassurance en
    // titre (verbe adapté annulation/remboursement), question « Souhaitez-vous continuer ? » en sous-titre.
    this.confirmService.confirm({
      validate: onValidate,
      cancel: onCancel,
      iconName: 'type=danger',
      title: this.translateService.instant(
        isRefund
          ? 'orders.info.zeroAmountFormulaWarningRefund'
          : 'orders.info.zeroAmountFormulaWarningCancel',
      ),
      subtitle: this.translateService.instant('orders.info.zeroAmountFormulaWarningContinue'),
      cancelBtnLabel: this.translateService.instant('shared.actions.cancel'),
      validateBtnLabel: this.translateService.instant(
        isRefund ? 'orders.validateRefund' : 'orders.validateCancellation',
      ),
    });
  }

  showErrorDialog(totalCount: number, label: string): void {
    this.confirmService.confirm({
      iconName: 'type=danger',
      title: this.translateService.instant(this.tableLabels.selectAllErrorTitle, {
        totalCount,
        itemLabel: label,
      }),
      subtitle: this.translateService.instant(this.tableLabels.selectAllErrorSubtitle, {
        maxRowsCount: MAX_ROWS_COUNT_ERROR,
        itemLabel: label,
      }),
      validateBtnLabel: this.translateService.instant(this.tableLabels.selectAllValidateErrorBtn),
      isCancelHidden: true,
      isSubmitOnKeyEnter: true,
      validate: () => {},
    });
  }

  showPageSizeError(totalCount: number): void {
    const label = this.translateService.instant('shared.folders');
    this.showErrorDialog(totalCount, label);
  }

  showPageSizeWarning(totalCount: number, onValidate: () => void, onCancel: () => void): void {
    const label = this.translateService.instant('shared.folders');
    this.showWarningDialog(totalCount, label, onValidate, onCancel);
  }

  handleSelectAllValidation(totalCount: number, onValidate: () => void, onCancel: () => void): boolean {
    const label = this.translateService.instant(this.tableLabels.itemLabel);

    if (totalCount > MAX_ROWS_COUNT_ERROR) {
      this.showErrorDialog(totalCount, label);
      onCancel();
      return false;
    }

    if (totalCount > MAX_ROWS_COUNT_WARNING) {
      this.showWarningDialog(totalCount, label, onValidate, onCancel);
      return false;
    }

    return true;
  }
}

